import { formatMoney } from "./format";
import { estimatePayoutCents } from "./payout";

// A contest's tier chooses how its market splits the pool. The numbers are
// never hardcoded here: they arrive from the server (options and the admin
// read) and this module only labels and arranges them.

export type Tier = "standard" | "home_turf";
export type TierSplit = { rake_bps: number; producer_bps: number; backing_bps: number };

export const TIER_LABEL: Record<Tier, string> = { standard: "Standard", home_turf: "Home Turf" };

export function asTier(v: unknown): Tier {
  return v === "home_turf" ? "home_turf" : "standard";
}

// centsPerDollar reads basis points as cents of every dollar: 1500 is 15¢.
export const centsPerDollar = (bps: number) => `${bps / 100}¢`;

// What the producer keeps is the winner's cut plus the backing share. Copy
// leads with this, never with the platform fee.
export const producersKeep = (s: TierSplit) =>
  `Producers keep ${centsPerDollar(s.producer_bps + s.backing_bps)} of every $1`;

export const sameSplit = (a: TierSplit, b: TierSplit) =>
  a.rake_bps === b.rake_bps && a.producer_bps === b.producer_bps && a.backing_bps === b.backing_bps;

// The worked example: a $400 pot where fans back A $160, B $120, C $80, and
// D $40, and A wins. Integer cents with the server's floor division. The
// fans' share comes from estimatePayoutCents, the one implementation the
// slip and the calculator also use.
const EXAMPLE_STAKES = [16000, 12000, 8000, 4000];
const EXAMPLE_POOL = 40000;

export type WorkedExample = {
  platformCents: number;
  winnerCutCents: number;
  backingCents: number[];
  backingTotalCents: number;
  winnerTotalCents: number;
  fansCents: number;
  fansPerDollarCents: number;
};

export function workedExample(s: TierSplit): WorkedExample {
  const platformCents = Math.floor((EXAMPLE_POOL * s.rake_bps) / 10_000);
  const winnerCutCents = Math.floor((EXAMPLE_POOL * s.producer_bps) / 10_000);
  const backingCents = EXAMPLE_STAKES.map((stake) => Math.floor((stake * s.backing_bps) / 10_000));
  const backingTotalCents = backingCents.reduce((sum, c) => sum + c, 0);
  const take = s.rake_bps + s.producer_bps + s.backing_bps;
  const fansCents = estimatePayoutCents(EXAMPLE_POOL - EXAMPLE_STAKES[0], take, 0, EXAMPLE_STAKES[0]) ?? 0;
  return {
    platformCents,
    winnerCutCents,
    backingCents,
    backingTotalCents,
    winnerTotalCents: winnerCutCents + backingCents[0],
    fansCents,
    fansPerDollarCents: Math.round((fansCents * 100) / EXAMPLE_STAKES[0]),
  };
}

export const tierCopy = {
  label: "Tier",
  custom: "Custom",
  hint: {
    standard: "The platform can add a house bonus and back producers with house money.",
    home_turf: "No house bonus or house backing. Producers bring their own backers.",
  } as Record<Tier, string>,
  option: (tier: Tier, split: TierSplit) => `${TIER_LABEL[tier]} · ${producersKeep(split)}`,
  // The preview, in the order the panel shows it.
  previewPot: "On a $400 pot: fans back A $160, B $120, C $80, D $40. A wins.",
  previewBacking: (e: WorkedExample) =>
    `Backing share ${formatMoney(e.backingTotalCents)} (A ${formatMoney(e.backingCents[0])} · B ${formatMoney(
      e.backingCents[1],
    )} · C ${formatMoney(e.backingCents[2])} · D ${formatMoney(e.backingCents[3])})`,
  previewWinner: (e: WorkedExample) =>
    `Winner's cut ${formatMoney(e.winnerCutCents)}, so A takes ${formatMoney(e.winnerTotalCents)}`,
  previewFans: (e: WorkedExample) =>
    `Winning fans ${formatMoney(e.fansCents)} (${formatMoney(e.fansPerDollarCents)} back per $1)`,
  previewPlatform: (e: WorkedExample) => `Platform ${formatMoney(e.platformCents)}`,
};
